/**
 * Display labels for API enum / slug values (transport modes, container types, sizes).
 * Stored values stay unchanged; only UI labels are translated.
 */

export function transportModeLabel(t, mode) {
  if (mode == null || mode === '') return '-'
  const key = String(mode).toLowerCase()
  return t(`logistics.transportationMode.${key}`, {
    defaultValue: t(`Create-QMS.transportMode.${key}`, { defaultValue: String(mode) })
  })
}

export function containerTypeLabel(t, type) {
  if (type == null || type === '') return '-'
  const key = String(type).toLowerCase()
  return t(`logistics.containerType.${key}`, {
    defaultValue: t(`Create-QMS.containerTypeSubtitle.${key}`, { defaultValue: String(type) })
  })
}

export function documentTypeLabel(t, docKey) {
  if (docKey == null || docKey === '') return '-'
  return t(`Create-QMS.documentTypes.${docKey}`, { defaultValue: String(docKey) })
}

/** Stored dropdown value (often English) → translated label */
export function containerSizeLabel(t, storedValue) {
  if (storedValue == null || storedValue === '') return ''
  const entry = CONTAINER_SIZE_OPTIONS.find(o => o.value === storedValue)
  if (entry) return t(`logistics.containerSize.${entry.i18nKey}`, { defaultValue: storedValue })
  return storedValue
}

export function truckTypeLabel(t, storedValue) {
  if (storedValue == null || storedValue === '') return ''
  const entry = TRUCK_TYPE_OPTIONS.find(o => o.value === storedValue)
  if (entry) return t(`logistics.truckType.${entry.i18nKey}`, { defaultValue: storedValue })
  return storedValue
}

export const CONTAINER_SIZE_OPTIONS = [
  { value: "20' (ft)", i18nKey: 'size20ft' },
  { value: "40' (ft)", i18nKey: 'size40ft' },
  { value: "40' HC (High cube)", i18nKey: 'size40hc' },
  { value: "20' (ft) open top (Tarpaulin)", i18nKey: 'size20OpenTop' },
  { value: "40' (ft) open top (Tarpaulin)", i18nKey: 'size40OpenTop' },
  { value: "20' (ft) Hard top (Steel)", i18nKey: 'size20HardTop' },
  { value: "40' (ft) Hard top (Steel)", i18nKey: 'size40HardTop' },
  { value: "40' (ft) HC (High cube)Hard top (Steel)", i18nKey: 'size40hcHardTop' },
  { value: "20' (ft) flat rack", i18nKey: 'size20FlatRack' },
  { value: "40' (ft) flat rack", i18nKey: 'size40FlatRack' },
  { value: "20' (ft) Refrigerated", i18nKey: 'size20Reefer' },
  { value: "40' (ft) Refrigerated", i18nKey: 'size40Reefer' },
  { value: "20' (ft) Flatform", i18nKey: 'size20Platform' },
  { value: "40' (ft) Flatform", i18nKey: 'size40Platform' },
  { value: "20' (ft) Ventilated", i18nKey: 'size20Ventilated' },
  { value: "40' (ft) Ventilated", i18nKey: 'size40Ventilated' },
  { value: "20' (ft) Insulated", i18nKey: 'size20Insulated' },
  { value: "40' (ft) Insulated", i18nKey: 'size40Insulated' },
  { value: "20' (ft) Flexi Tank", i18nKey: 'size20FlexiTank' }
]

export const TRUCK_TYPE_OPTIONS = [
  { value: 'Curtainsider/Tarpaulin', i18nKey: 'curtainsider' },
  { value: 'Box', i18nKey: 'box' },
  { value: 'Refrigerated/Frigo', i18nKey: 'refrigerated' },
  { value: 'Taillift', i18nKey: 'taillift' },
  { value: 'Flatbed', i18nKey: 'flatbed' },
  { value: 'Dump', i18nKey: 'dump' },
  { value: 'Tanker', i18nKey: 'tanker' },
  { value: 'Dry Van', i18nKey: 'dryVan' },
  { value: 'Container Truck', i18nKey: 'containerTruck' },
  { value: 'Coil Trailer', i18nKey: 'coilTrailer' },
  { value: 'Open Platform', i18nKey: 'openPlatform' },
  { value: 'Jumbo', i18nKey: 'jumbo' },
  { value: 'Log Truck', i18nKey: 'logTruck' },
  { value: 'Cattle Truck', i18nKey: 'cattleTruck' },
  { value: 'Van (Regular)', i18nKey: 'vanRegular' }
]

/** Values sent to API (English) for size / truck dropdowns */
export const containerSizeValues = CONTAINER_SIZE_OPTIONS.map(o => o.value)
export const truckTypeValues = TRUCK_TYPE_OPTIONS.map(o => o.value)

export function serviceCategoryLabel(t, categoryKey, fallbackLabel) {
  if (!categoryKey) return fallbackLabel || '-'
  return t(`logistics.serviceCategory.${categoryKey}`, {
    defaultValue: fallbackLabel || categoryKey
  })
}

export function regionLabel(t, regionKey, fallbackLabel) {
  if (!regionKey) return fallbackLabel || '-'
  return t(`logistics.region.${regionKey}`, { defaultValue: fallbackLabel || regionKey })
}

export function yesNoLabel(t, value) {
  if (value == null || value === '') return '-'
  const v = String(value).toLowerCase()
  if (v === 'yes') return t('common.yes')
  if (v === 'no') return t('common.no')
  if (v === 'not sure' || v === 'not_sure' || v === 'notsure') return t('common.notSure')
  return value
}

export function goodsCategoryLabel(t, name) {
  if (!name) return '-'

  const i18nKey = `logistics.goodsCategory.${slugify(name)}`
  const i18nLabel = t(i18nKey)
  if (i18nLabel && i18nLabel !== i18nKey) {
    return i18nLabel
  }

  const lang = String(t.language || 'en').split('-')[0]
  const cached = goodsCategoryLabelCache[name]
  if (cached?.[lang]) return cached[lang]
  if (cached?.en) return cached.en
  return name
}

const goodsCategoryLabelCache = {}

export function registerGoodsCategoryLabels(categories) {
  if (!Array.isArray(categories)) return

  categories.forEach(category => {
    const name = category?.name || category?.title
    if (!name) return

    goodsCategoryLabelCache[name] = {
      en: category?.labels?.en || name,
      fr: category?.labels?.fr || category?.labels?.en || name
    }
  })
}

function slugify(text) {
  return String(text)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 72)
}
